/**
 * M1o2 drag carousel - mouse drag-to-scroll for any horizontal card scroller.
 * Same behaviour as the home "Daily Hustle" strip (assets/m1o2-product-card-hero.js), made
 * reusable: mark the overflow-x track with [data-m1o2-drag-track]; its direct children are
 * the cards it snaps to.
 *
 * The track stays a native overflow-x scroller, so swipe, trackpad, wheel and keyboard keep
 * working on their own. This only adds mouse drag: mouse pointers only (touch already
 * scrolls natively), inert when the track isn't overflowing (e.g. the mobile stack), snap
 * suspended while dragging, a release that settles on the nearest card (a flick always
 * advances at least one), and the click that ends a real drag is swallowed so it can't
 * follow a card link or press a pill / ADD TO CART.
 */
(function () {
	'use strict';

	var DRAG_THRESHOLD = 5;
	/* A release this far past the start (px) advances at least one card, carousel-style. */
	var FLICK_DISTANCE = 40;

	function initDrag(track) {
		var startX = 0;
		var lastX = 0;
		var startScroll = 0;
		var pointerId = null;
		var dragging = false;
		var suppressClick = false;
		var restoreTimer = null;

		function scrollable() {
			return track.scrollWidth > track.clientWidth + 1;
		}

		/* Snap + smooth scrolling are switched off INLINE: with scroll-snap still active,
		   every scrollLeft write is snapped straight back, so the track never moves. */
		function freeScroll() {
			clearTimeout(restoreTimer);
			track.style.scrollSnapType = 'none';
			track.style.scrollBehavior = 'auto';
		}

		function restoreSnap() {
			track.style.scrollSnapType = '';
			track.style.scrollBehavior = '';
		}

		/* scrollLeft values at which each card sits flush with the track's start padding
		   (the same position CSS scroll-snap would pick). */
		function snapPoints() {
			var pad = parseFloat(getComputedStyle(track).paddingLeft) || 0;
			var trackLeft = track.getBoundingClientRect().left;
			var max = track.scrollWidth - track.clientWidth;
			var points = [];
			Array.prototype.forEach.call(track.children, function (card) {
				var left = track.scrollLeft + card.getBoundingClientRect().left - trackLeft - pad;
				points.push(Math.max(0, Math.min(max, Math.round(left))));
			});
			return points;
		}

		function settle(dx) {
			var points = snapPoints();
			var current = track.scrollLeft;
			var target = current;
			if (points.length) {
				var nearest = 0;
				points.forEach(function (p, i) {
					if (Math.abs(p - current) < Math.abs(points[nearest] - current)) nearest = i;
				});
				var index = nearest;
				/* Dragging left (dx < 0) moves forward. Guarantee a flick lands on a new card
				   in the drag's direction rather than bouncing back to where it started. */
				if (dx <= -FLICK_DISTANCE && points[index] <= startScroll) index++;
				if (dx >= FLICK_DISTANCE && points[index] >= startScroll) index--;
				index = Math.max(0, Math.min(points.length - 1, index));
				target = points[index];
			}
			track.scrollTo({ left: target, behavior: 'smooth' });
			/* Re-enable CSS snap only once the glide has finished, or it would cut it short. */
			restoreTimer = setTimeout(restoreSnap, 600);
		}

		function endDrag() {
			if (pointerId === null) return;
			var wasDragging = dragging;
			var id = pointerId;
			pointerId = null;
			dragging = false;
			track.classList.remove('is-dragging');
			if (wasDragging) {
				if (track.hasPointerCapture(id)) track.releasePointerCapture(id);
				settle(lastX - startX);
			}
		}

		track.addEventListener('pointerdown', function (event) {
			if (event.pointerType !== 'mouse' || event.button !== 0 || !scrollable()) return;
			pointerId = event.pointerId;
			startX = lastX = event.clientX;
			startScroll = track.scrollLeft;
			dragging = false;
			suppressClick = false;
		});

		track.addEventListener('pointermove', function (event) {
			if (event.pointerId !== pointerId) return;
			lastX = event.clientX;
			var dx = lastX - startX;
			if (!dragging) {
				if (Math.abs(dx) < DRAG_THRESHOLD) return;
				/* Capture only once it's a real drag, so a plain click keeps its own target. */
				dragging = true;
				suppressClick = true;
				freeScroll();
				startScroll = track.scrollLeft;
				startX = lastX;
				dx = 0;
				track.setPointerCapture(pointerId);
				track.classList.add('is-dragging');
			}
			event.preventDefault();
			track.scrollLeft = startScroll - dx;
		});

		track.addEventListener('pointerup', endDrag);
		track.addEventListener('pointercancel', endDrag);
		track.addEventListener('lostpointercapture', endDrag);

		track.addEventListener(
			'click',
			function (event) {
				if (!suppressClick) return;
				suppressClick = false;
				event.preventDefault();
				event.stopPropagation();
			},
			true
		);

		/* Native image/link drag and text selection would hijack the gesture. */
		track.addEventListener('dragstart', function (event) {
			event.preventDefault();
		});
	}

	function initTracks() {
		var tracks = document.querySelectorAll('[data-m1o2-drag-track]:not([data-m1o2-drag-ready])');
		Array.prototype.forEach.call(tracks, function (track) {
			track.setAttribute('data-m1o2-drag-ready', '');
			initDrag(track);
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initTracks);
	} else {
		initTracks();
	}
	/* Theme editor re-renders sections in place. */
	document.addEventListener('shopify:section:load', initTracks);
})();
